import React, { Component, Dispatch } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';

import './PaypalPayment.scss';

import { formatCurrency } from 'formatters';
import { appConfig } from 'configurations';
import { AppState } from 'store';
import { generateDispatchToPropsMap, externalScriptHelper, paymentHelper } from 'helpers';
import {
    PaypalSetupPaymentFunc,
    paypalSetupPayment,
    paypalReject,
    PaypalRejectFunc,
    PaypalCompletePaymentFunc,
    paypalCompletePayment,
    paypalSetAuthResult,
    PaypalSetAuthResultFunc
} from 'store/payment/actions/paypal';
import { PaymentDetailsModel, PaypalAuthModel } from 'models/Payment';
import { SofaButton } from '@sofology/sofologycomponents';

interface OwnProps {
}

interface StateProps {
    paypalPaymentCode: string | null;
    isPaypalPaymentSetupInProgress: boolean;
    paymentDetails: PaymentDetailsModel;
    paypalAuthResult: PaypalAuthModel | null;
}

interface DispatchProps {
    paypalSetupPayment: PaypalSetupPaymentFunc;
    paypalCompletePayment: PaypalCompletePaymentFunc;
    paypalReject: PaypalRejectFunc;
    paypalSetAuthResult: PaypalSetAuthResultFunc;
}

interface OwnState {
    getPaymentCodePromiseResolver: { resolve: (value: string) => void; reject: () => void } | null;
}

class PaypalPayment extends Component<OwnProps & StateProps & DispatchProps, OwnState> {
    private paypalComponent = React.createRef<HTMLDivElement>();

    constructor(props: OwnProps & StateProps & DispatchProps) {
        super(props);

        this.state = {
            getPaymentCodePromiseResolver: null
        };
    }

    componentDidUpdate(prevProps: OwnProps & StateProps & DispatchProps) {
        if (this.state.getPaymentCodePromiseResolver && prevProps.isPaypalPaymentSetupInProgress && !this.props.isPaypalPaymentSetupInProgress) {
            if (this.props.paypalPaymentCode) {
                this.state.getPaymentCodePromiseResolver.resolve(this.props.paypalPaymentCode);
            } else {
                this.state.getPaymentCodePromiseResolver.reject();
                this.setState({ getPaymentCodePromiseResolver: null });
            }
        }
    }

    handleGetPaymentCode = () => {
        return new Promise((resolve, reject) => {
            this.setState({ getPaymentCodePromiseResolver: { resolve, reject } });
            this.props.paypalSetupPayment(paymentHelper.getDeposit(this.props.paymentDetails));
        });
    };

    handleOnAuthorize = (data: { payerID: string; paymentID: string }) => {
        this.props.paypalSetAuthResult({ payerId: data.payerID, paymentId: data.paymentID });
    };

    handleOnCancel = () => {
        this.props.paypalReject(false);
    };

    handleOnError = () => {
        this.props.paypalReject(true);
    };

    handlePaySecurelyClick = () => {
        this.props.paypalCompletePayment();
    };

    render(): JSX.Element {
        const isAuthorized = Boolean(this.props.paypalAuthResult);

        return (
            <div className='paypal-payment' ref={this.paypalComponent}>
                <div className='paypal-payment__title'>Payment via PayPal today:</div>
                <div className='paypal-payment__price'>{formatCurrency(paymentHelper.getDeposit(this.props.paymentDetails))}</div>
                {isAuthorized
                    ? this.renderPaySecurelyButton()
                    : this.renderPaypalButton()
                }
            </div>
        );
    }

    renderPaypalButton(): JSX.Element {
        const paypal = externalScriptHelper.getExternalScript<any>('paypal');

        // Driver exists in Paypal button object only for data-version-4
        const PaypalButton = paypal.Button.driver('react', { React, ReactDOM });

        return (
            <div className='paypal-payment__paypal-button'>
                <PaypalButton
                    env={appConfig.paypal.env}
                    client={appConfig.paypal.client}
                    commit={false}
                    payment={this.handleGetPaymentCode}
                    onAuthorize={this.handleOnAuthorize}
                    onCancel={this.handleOnCancel}
                    onError={this.handleOnError}
                    style={{
                        size: 'responsive',
                        color: 'gold',
                        shape: 'pill',
                        label: 'pay',
                        tagline: 'false'
                    }}
                />
            </div>
        );
    }

    renderPaySecurelyButton(): JSX.Element {
        return (
            <div className='paypal-payment__pay-securely'>
                <div className='paypal-payment__pay-securely-message'>You have been authorised with PayPal, click "Pay Securely" to complete your order</div>
                <div className='paypal-payment__pay-securely-button'>
                    <SofaButton
                        text={<span><i className='sa sa-lock'></i> Pay securely</span>}
                        variant='primary'
                        onClick={this.handlePaySecurelyClick}
                    />
                </div>
            </div>
        );
    }
}

function mapStateToProps(state: AppState): StateProps {
    return {
        paypalPaymentCode: state.paymentState.paypalPaymentCode,
        isPaypalPaymentSetupInProgress: state.paymentState.isPaypalPaymentSetupInProgress,
        paymentDetails: state.paymentState.paymentDetails,
        paypalAuthResult: state.paymentState.paypalAuthSuccessfullResult
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(dispatch, [
        paypalSetupPayment,
        paypalCompletePayment,
        paypalReject,
        paypalSetAuthResult
    ]);
}

export default connect(mapStateToProps, mapDispatchToProps)(PaypalPayment);